"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlobalFilterSchema = void 0;
var zod_1 = require("zod");
/**
 * 국내숙소, 레저 plp/srp 에서 사용하는 global filter 스키마 정의
 * */
/** =============== selection 공통 스키마 =========== */
var BaseFilterItemSchema = zod_1.z.object({
    key: zod_1.z.string(),
    selected: zod_1.z.boolean(),
});
var BooleanFilterItemSchema = BaseFilterItemSchema.extend({
    type: zod_1.z.literal('PRODUCT_STATUS'),
});
var CodeFilterItemSchema = BaseFilterItemSchema.extend({
    type: zod_1.z.literal('CODE'),
    code: zod_1.z.string(),
    isSelectAllItem: zod_1.z.boolean().nullish(),
    title: zod_1.z.string(),
    iconUrl: zod_1.z.string().nullish(),
});
var NumberRangeFilterItemSchema = BaseFilterItemSchema.extend({
    type: zod_1.z.literal('NUMBER_RANGE'),
    value: zod_1.z.object({
        from: zod_1.z.number(),
        to: zod_1.z.number(),
    }),
});
/** ====================== 필터 구분 ======================*/
var CommonGlobalFilterItemSchema = zod_1.z.object({
    /** 필터 타이틀 */
    title: zod_1.z.string(),
    /** 글로벌 필터 바텀시트 탭에 노출할 타이틀. */
    shortTitle: zod_1.z.string().nullish(),
    logMeta: zod_1.z.record(zod_1.z.unknown()).nullish(),
    serverLogMeta: zod_1.z.record(zod_1.z.unknown()).nullish(),
});
var SubTitleSchema = zod_1.z.object({
    type: zod_1.z.string(),
    value: zod_1.z.array(zod_1.z.object({ text: zod_1.z.string(), isBold: zod_1.z.boolean() })),
});
var SwitchGlobalFilterItemSchema = CommonGlobalFilterItemSchema.extend({
    type: zod_1.z.literal('SWITCH'),
    items: zod_1.z.array(BooleanFilterItemSchema),
    /** 필터 하위 아이템 중 선택된 항목이 있는지 여부 */
    hasSelectedItems: zod_1.z.boolean().nullish(),
});
var ChipGlobalFilterItemSchema = CommonGlobalFilterItemSchema.extend({
    type: zod_1.z.literal('CHIPS'),
    items: zod_1.z.array(CodeFilterItemSchema),
    /** 필터 하위 아이템 중 선택된 항목이 있는지 여부 */
    hasSelectedItems: zod_1.z.boolean().nullish(),
});
var CheckBoxListGlobalFilterItemSchema = CommonGlobalFilterItemSchema.extend({
    type: zod_1.z.literal('CHECKBOX_LIST'),
    items: zod_1.z.array(CodeFilterItemSchema),
    /** 필터 하위 아이템 중 선택된 항목이 있는지 여부 */
    hasSelectedItems: zod_1.z.boolean().nullish(),
});
var BaseHistogramNumberRangeGlobalFilterItemSchema = CommonGlobalFilterItemSchema.extend({
    items: zod_1.z.array(NumberRangeFilterItemSchema),
    subTitle: SubTitleSchema.nullish(),
    bins: zod_1.z.array(zod_1.z.object({ from: zod_1.z.number(), to: zod_1.z.number(), count: zod_1.z.number() })).nullish(),
    range: zod_1.z.object({ max: zod_1.z.number(), min: zod_1.z.number() }),
    countRange: zod_1.z.object({ max: zod_1.z.number(), min: zod_1.z.number() }).nullish(),
    hasSelectedItems: zod_1.z.boolean().nullish(),
});
/**
 * 히스토그램 숫자 범위 글로벌 필터 아이템 스키마
 * NOTE: 현재기준 국내숙소필터만 subTitle을 사용하고, 레저에서는 사용하지 않음
 * 추가적인 카테고리에서 어떻게 구현하게 되는지에 따라 수정이 필요할 수 있음
 * @see {@link https://docs.google.com/document/d/1WwtY9Q63RrXv7Cyilcf3-TpMEL3gSp8RpkcR0hgamkQ/edit?pli=1&tab=t.0#heading=h.22eivrv1o9ly}
 * */
var HistogramNumberRangeGlobalFilterItemSchema = BaseHistogramNumberRangeGlobalFilterItemSchema.extend({
    type: zod_1.z.literal('HISTOGRAM_NUMBER_RANGE'),
});
/**
 * 히스토그램 숫자 범위 해외숙소 필터 아이템 스키마
 * NOTE: 해외숙소에서는 bins를 사용하지 않음
 * @see {@link https://yanoljagroup.atlassian.net/browse/YAFE-2469?focusedCommentId=2348055}
 * */
var HistogramNumberRangeWithoutUpperLimitGlobalFilterItemSchema = BaseHistogramNumberRangeGlobalFilterItemSchema.extend({
    type: zod_1.z.literal('FLAT_NUMBER_RANGE_WITHOUT_UPPER_LIMIT'),
});
/**
 * 아코디언 형태의 글로벌 필터 아이템 스키마
 * NOTE: 현재 구현은 2depth까지만 지원 3depth 이상 필요하다면 수정 필요
 * */
var AccordionGlobalFilterItemSchema = CommonGlobalFilterItemSchema.extend({
    type: zod_1.z.literal('ACCORDION'),
    items: zod_1.z.array(zod_1.z.object({
        title: zod_1.z.string(),
        items: zod_1.z.array(CodeFilterItemSchema),
        hasSelectedItems: zod_1.z.boolean().nullish(),
        type: zod_1.z.literal('ACCORDION_ITEM'),
    })),
    hasSelectedItems: zod_1.z.boolean().nullish(),
});
/**
 * 글로벌 필터 아이템 스키마
 * @see {@link https://docs.google.com/document/d/1WwtY9Q63RrXv7Cyilcf3-TpMEL3gSp8RpkcR0hgamkQ/edit?pli=1&tab=t.0#heading=h.f8xrrfvj29wz}
 * */
var GlobalFilterItemSchema = zod_1.z.union([
    SwitchGlobalFilterItemSchema,
    ChipGlobalFilterItemSchema,
    CheckBoxListGlobalFilterItemSchema,
    HistogramNumberRangeGlobalFilterItemSchema,
    AccordionGlobalFilterItemSchema,
    HistogramNumberRangeWithoutUpperLimitGlobalFilterItemSchema,
]);
/** ======= 각 카테고리별 글로벌 필터 =========*/
exports.GlobalFilterSchema = zod_1.z.object({
    items: zod_1.z.array(GlobalFilterItemSchema),
    logMeta: zod_1.z.record(zod_1.z.unknown()).nullish(),
    ctaButtonDefaultText: zod_1.z.string(),
    serverLogMeta: zod_1.z.record(zod_1.z.unknown()).nullish(),
});
